<script setup>
import { ref } from 'vue'
import { useShopifyStore } from '@stores/shopify.js'
import Logo from '@components/logos/Logo.vue'
import ArrowUpRight from '@components/icons/ArrowUpRight.vue'
import Loading from '@components/icons/Loading.vue'
import Instagram from '@components/icons/Instagram.vue'

const shopify = useShopifyStore()

const processing = ref(false)
const success = ref(false)
const klaviyo = ref({})
const errors = ref({})

function submit(form) {
	form.preventDefault()
	if (!processing.value) {
		processing.value = true
		axios
			.post('/api/newsletter', klaviyo.value)
			.then(() => {
				success.value = true
				processing.value = false
				errors.value = []
			})
			.catch((error) => {
				if (error.response.status == 422) {
					processing.value = false
					errors.value = error.response.data.error
					console.error(error.response)
				}
			})
	}
}

const links = ref([
	{
		label: 'Home',
		to: { name: 'index' },
	},
	{
		label: 'About',
		to: { name: 'about' },
	},
	{
		label: 'Privacy Policy',
		to: { name: 'policy', params: { slug: 'privacy-policy' } },
	},
	{
		label: 'Terms And Conditions',
		to: { name: 'policy', params: { slug: 'terms-of-service' } },
	},
])
</script>

<template>
	<footer class="bg-bg-inverted text-text-inverted relative isolate overflow-hidden py-48">
		<div class="container">
			<div class="grid grid-cols-12 gap-x-16 gap-y-32 lg:gap-y-48">
				<div class="col-span-12 flex justify-start lg:mb-32">
					<router-link :to="{ name: 'index' }">
						<span class="sr-only">Home</span>
						<Logo class="h-36 w-auto" />
					</router-link>
				</div>
				<div class="col-span-12 flex flex-col items-start gap-24 lg:col-span-3">
					<div class="flex flex-col gap-8">
						<p class="font-primary body-md uppercase">Newsletter</p>
						<p class="body-md text-text-muted">Sign up for offers, product news, and tips.</p>
					</div>
					<div v-if="success" class="text-text-inverted flex items-center gap-5">
						<svg class="h-20 w-20 fill-white/50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
							<path d="M320 576C178.6 576 64 461.4 64 320C64 178.6 178.6 64 320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576zM288 350.1L224 286.1L190.1 320L271.1 401C280.5 410.4 295.7 410.4 305 401L450 256L416.1 222.1L288.1 350.1z" />
						</svg>
						<span class="body-md">Thank you for subscribing!</span>
					</div>
					<form v-else class="flex w-full rounded-lg bg-white/10" action="" @submit="submit">
						<label for="newsletter-email" class="sr-only">Email address</label>
						<input id="newsletter-email" class="body-md text-text-inverted placeholder:text-text-muted h-40 w-full border border-transparent px-16 transition-all duration-300" type="email" placeholder="Enter your email" v-model="klaviyo.email" required />
						<button class="font-primary body-md flex size-40 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white transition-colors duration-300 hover:bg-white/20" type="submit">
							<span class="sr-only">Subscribe to newsletter</span>
							<Loading class="size-18 animate-spin" v-if="processing" />
							<ArrowUpRight class="size-20" v-else />
						</button>
					</form>
				</div>
				<div class="col-span-12 flex flex-col gap-24 lg:col-span-6 lg:col-start-1 xl:col-span-2 xl:col-start-5">
					<p class="font-primary body-md uppercase">Sitemap</p>
					<ul class="body-md flex flex-col gap-16">
						<li v-for="link in links" :key="link.label">
							<a v-if="link.href" :href="link.href" target="_blank" rel="noopener noreferrer" class="text-text-muted hover:text-text-inverted transition-colors">
								{{ link.label }}
							</a>
							<router-link v-else :to="link.to" class="text-text-muted hover:text-text-inverted transition-colors">
								{{ link.label }}
							</router-link>
						</li>
					</ul>
				</div>
				<div class="col-span-12 flex flex-col gap-24 lg:col-span-6 xl:col-span-4">
					<p class="font-primary body-md uppercase">Collectons</p>
					<ul class="body-md grid grid-cols-2 gap-16 lg:grid-cols-3">
						<li v-for="collection in shopify.teamsCollections" :key="collection.id">
							<router-link
								:to="{
									name: 'collection',
									params: { slug: collection.slug },
								}"
								@click="global.toggleMobile(false)"
								class="text-text-muted hover:text-text-inverted transition-colors">
								<span>{{ collection.title }}</span>
							</router-link>
						</li>
					</ul>
				</div>
				<div class="flex flex-col gap-24 xl:col-span-2">
					<p class="font-primary body-md uppercase">Socials</p>
					<ul>
						<li>
							<a href="https://www.instagram.com/ghostvisn/" target="_blank" rel="noopener noreferrer" class="flex size-40 items-center justify-center rounded-lg bg-white/10 transition-colors duration-300 hover:bg-white/20">
								<span class="sr-only">Instagram</span>
								<Instagram class="size-20" />
							</a>
						</li>
					</ul>
				</div>
				<div class="col-span-12 flex items-center">
					<p class="body-md text-text-muted">&copy; Ghost Vision {{ new Date().getFullYear() }}. All rights reserved.</p>
				</div>
			</div>
		</div>
		<Logo class="absolute -top-48 -right-148 -z-1 hidden h-151 w-auto opacity-10 lg:block" />
	</footer>
</template>
